import Alpine from "alpinejs"
import collapse from "@alpinejs/collapse"

Alpine.plugin(collapse)

Alpine.data("grippeimpfcheck", (config) => ({
    activeStep: 1,
    isLoading: false,
    steps: Object.values(config.steps),
    formData: {},
    formDataTranslated: {},
    currentResult: null,
    isPdfGenerating: false,
    apiError: null,

    init() {
        this.$watch("activeStep", () => {
            this.updateStepperUI()
            this.saveState()
            this.updateBodyClass() // Add body class updates

            // If we're at the result step, determine which result to show
            if (this.activeStep === this.steps.length) {
                this.determineResult()
            }
        })

        // Listen for dropdown events
        document.addEventListener("dropdown:add-item", (event) => {
            const { identifier, title } = event.detail
            // In the age dropdown question (2nd question), we're always on step 3
            // This will ensure the dropdown values are captured properly regardless of id
            if (this.activeStep === 3) {
                this.formData.question_2 = identifier
                this.formDataTranslated.question_2 = title
                this.saveState()
            }
        })

        document.addEventListener("dropdown:clear-items", (event) => {
            // In the age dropdown question (2nd question), we're always on step 3
            // This will ensure the dropdown values are cleared properly regardless of id
            if (this.activeStep === 3) {
                this.formData.question_2 = null
                this.formDataTranslated.question_2 = null
                this.saveState()
            }
        })

        // State restoration logic
        const saved = localStorage.getItem("grippeimpfcheck")
        if (saved) {
            try {
                const state = JSON.parse(saved)
                this.formData = state.formData || {}
                this.formDataTranslated = state.formDataTranslated || {}
                // Only restore step if it's a valid step
                if (state.activeStep && state.activeStep <= this.steps.length) {
                    this.activeStep = state.activeStep
                }
                if (state.currentResult) {
                    this.currentResult = state.currentResult
                }
                if (state.posterId) {
                    this.posterId = state.posterId
                }
                if (state.posterToken) {
                    this.posterToken = state.posterToken
                }
            } catch (e) {
                localStorage.removeItem("grippeimpfcheck")
            }
        }
        this.updateBodyClass()
    },

    // update body class based on activeStep
    updateBodyClass() {
        this.activeStep !== 1
            ? document.body.classList.add("activestepper")
            : document.body.classList.remove("activestepper")
    },

    get currentStep() {
        return this.activeStep - 1
    },

    get totalSteps() {
        return this.steps.length
    },

    // Get the component that should be visible
    get visibleComponent() {
        if (this.activeStep === this.steps.length) {
            return "result_" + this.currentResult
        }
        return this.steps[this.currentStep]?.component
    },

    // Check if user has made a selection on the current step
    get canProceed() {
        const currentComponent = this.steps[this.currentStep]?.component

        if (currentComponent === "start") {
            return true // Start page can always proceed
        }

        if (currentComponent === "result") {
            return true // Result page doesn't need validation
        }

        // For question steps, verify the appropriate value exists
        if (currentComponent?.startsWith("question_")) {
            const questionName = currentComponent

            // For the dropdown (age question)
            if (questionName === "question_2") {
                return !!this.formData[questionName]
            }

            // For radio options
            return !!this.formData[questionName]
        }

        return false
    },

    // Determine if a specific component should be visible
    isVisible(componentName) {
        // For result pages, check against the current result
        if (componentName.startsWith("result_")) {
            const resultNumber = parseInt(componentName.split("_")[1])
            return this.activeStep === this.steps.length && this.currentResult === resultNumber
        }

        // For other components, check against the current active step
        return this.visibleComponent === componentName
    },

    async prev() {
        if (this.activeStep > 1) {
            this.activeStep--
            // Reset currentResult when going back from results
            if (this.activeStep < this.steps.length) {
                this.currentResult = null
            }
            // Scroll body to top on step change
            this.scrollToTop()
        }
    },

    async next() {
        if (this.canProceed) {
            if (this.activeStep < this.totalSteps) {
                // Special logic for skipping questions based on previous answers
                if (this.shouldSkipToResult()) {
                    this.skipToResult()
                } else {
                    this.activeStep++
                }
                // Scroll body to top on step change
                this.scrollToTop()
            }
        }
    },

    // Check if we should skip to result based on the flow chart logic
    shouldSkipToResult() {
        // If answered "Yes" to question 1 (poultry contact), skip to result 10
        if (this.steps[this.currentStep]?.component === "question_1" && this.formData.question_1 === "Ja") {
            return true
        }

        // If age is 0-6 months, skip to result 1
        if (this.steps[this.currentStep]?.component === "question_2" && this.formData.question_2 === "0-6") {
            return true
        }

        // If age is 65+, skip to result 2
        if (this.steps[this.currentStep]?.component === "question_2" && this.formData.question_2 === "65+") {
            return true
        }

        return false
    },

    // Skip to appropriate result based on flow chart logic
    skipToResult() {
        // If answered "Yes" to question 1 (poultry contact), go to result with recommendation 10
        if (this.steps[this.currentStep]?.component === "question_1" && this.formData.question_1 === "Ja") {
            this.activeStep = this.steps.length // Go to result step
            return
        }

        // If age is 0-6 months, go to result with recommendation 1
        if (this.steps[this.currentStep]?.component === "question_2" && this.formData.question_2 === "0-6") {
            this.activeStep = this.steps.length // Go to result step
            return
        }

        // If age is 65+, skip to result 2
        if (this.steps[this.currentStep]?.component === "question_2" && this.formData.question_2 === "65+") {
            this.activeStep += 2
            return
        }

        // Otherwise just proceed to next question
        this.activeStep++
    },

    determineResult() {
        // If already calculated from a skip condition
        if (this.currentResult) {
            return
        }

        let suggestions = []
        // Collect all applicable suggestions based on answers
        if (this.formData.question_1 === "Ja") {
            suggestions.push("0")
        } else {
            if (this.formData.question_2 === "0-6") {
                suggestions.push("1")
            } else {
                if (this.formData.question_2 !== "65+" && this.formData.question_2 !== "65+") {
                    suggestions.push("9")

                    if (this.formData.question_3 === "Ja") {
                        suggestions.push("2")
                    }
                    if (this.formData.question_3 !== "Ja") {
                        suggestions.push("9")
                    }
                } else if (this.formData.question_2 === "65+") {
                    suggestions.push("2")
                }

                if (this.formData.question_4 === "Ja") {
                    suggestions.push("2")
                }
                if (this.formData.question_4 !== "Ja") {
                    suggestions.push("9")
                }
                if (this.formData.question_5 === "Ja") {
                    suggestions.push("3")
                }
                if (this.formData.question_5 !== "Ja") {
                    suggestions.push("9")
                }
                if (this.formData.question_6 === "Ja") {
                    suggestions.push("5")
                }
                if (this.formData.question_6 !== "Ja") {
                    suggestions.push("9")
                }
            }
        }
        // Remove duplicates
        suggestions = [...new Set(suggestions)]

        let zeroBasedIndexResultFromClientDocumentation = -1

        if (suggestions.indexOf("0") !== -1) {
            console.log('here')
            zeroBasedIndexResultFromClientDocumentation = 0
        } else if (suggestions.indexOf("1") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 1
        } else if (
            suggestions.indexOf("2") !== -1 &&
            suggestions.indexOf("3") !== -1 &&
            suggestions.indexOf("5") !== -1
        ) {
            zeroBasedIndexResultFromClientDocumentation = 7
        } else if (suggestions.indexOf("2") !== -1 && suggestions.indexOf("3") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 4
        } else if (suggestions.indexOf("2") !== -1 && suggestions.indexOf("5") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 8
        } else if (suggestions.indexOf("3") !== -1 && suggestions.indexOf("5") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 6
        } else if (suggestions.indexOf("2") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 2
        } else if (suggestions.indexOf("3") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 3
        } else if (suggestions.indexOf("4") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 4
        } else if (suggestions.indexOf("5") !== -1) {
            zeroBasedIndexResultFromClientDocumentation = 5
        } else {
            zeroBasedIndexResultFromClientDocumentation = 9
        }
        this.currentResult = zeroBasedIndexResultFromClientDocumentation + 1
    },

    handleFormUpdate(field, event) {
        this.formData[field] = event.target.type === "checkbox" ? event.target.checked : event.target.value
        this.formDataTranslated[field] = event.target.parentElement.textContent
        this.saveState()
    },

    // UI Update methods
    updateStepperUI() {
        if (!this.$refs.stepper) return

        const steps = this.$refs.stepper.querySelectorAll("li")
        steps.forEach((step, index) => {
            const stepNum = index + 1
            step.classList.remove("done", "active", "disabled")

            if (stepNum < this.activeStep) {
                step.classList.add("done")
            } else if (stepNum === this.activeStep) {
                step.classList.add("active")
            } else {
                step.classList.add("disabled")
            }
        })
    },

    // Check if a step is accessible
    isStepAccessible(stepNumber) {
        return stepNumber <= this.activeStep
    },

    goToStep(stepNumber) {
        if (stepNumber <= this.activeStep && stepNumber > 0) {
            this.activeStep = stepNumber
            // Reset result when going back from results page
            if (stepNumber < this.steps.length) {
                this.currentResult = null
            }
        }
    },

    // Start over - reset everything and clear localStorage
    startOver() {
        // Clear all form data
        this.formData = {}
        this.formDataTranslated = {}
        this.currentResult = null
        this.posterId = null
        this.posterToken = null
        this.activeStep = 1

        // Clear localStorage
        localStorage.removeItem("grippeimpfcheck")

        // Dispatch generic reset event for all form components
        document.dispatchEvent(
            new CustomEvent("form:reset", {
                detail: {
                    formId: "grippeimpfcheck", // Identify which form is being reset
                    filterId: null, // null means reset all dropdowns
                },
            }),
        )

        // Update UI
        this.updateStepperUI()
        this.updateBodyClass() // Make sure body class is updated when starting over
    },

    scrollToTop() {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        })
    },

    saveState() {
        const stateToSave = {
            activeStep: this.activeStep,
            formData: this.formData,
            formDataTranslated: this.formDataTranslated,
            currentResult: this.currentResult,
            posterId: this.posterId,
            posterToken: this.posterToken,
        }
        localStorage.setItem("grippeimpfcheck", JSON.stringify(stateToSave))
    },
}))

Alpine.data("expandableContent", (config = {}) => ({
    expanded: false,
    hasEnoughContent: false,

    init() {
        this.$nextTick(() => {
            this.checkContentSize()
        })
    },

    checkContentSize() {
        const copyElement = this.$refs.copyContent || this.$el.querySelector('[x-ref="copyContent"]')
        if (copyElement) {
            // Check if content height exceeds threshold or character count
            const contentHeight = copyElement.scrollHeight
            const textLength = copyElement.textContent.length

            // Configurable thresholds
            const heightThreshold = config.heightThreshold || 200
            const textThreshold = config.textThreshold || 200

            // Show expand if content is taller than threshold or has more than threshold characters
            this.hasEnoughContent = contentHeight > heightThreshold || textLength > textThreshold
        }
    },

    toggle() {
        this.expanded = !this.expanded
    },

    get toggleText() {
        const showLessText = config.showLessText || "Show less"
        const showMoreText = config.showMoreText || "Show more"
        return this.expanded ? showLessText : showMoreText
    },
}))
